import { t } from "../i18n"
// offline_form_controller.js — intercepts form submission when offline
// Queues the form data to IndexedDB via the taagger:queue-mutation custom event
// Works for any form: tasks, comments, etc.
// Optional offlineUrl value: when set, POST to this URL instead of form.action when offline

import { Controller } from '@hotwired/stimulus';
import { enqueue } from '../offline_db';

export default class extends Controller {
  static values = { companyId: Number, taskId: String, offlineUrl: String, description: String }

  connect() {
    this._submitHandler = (e) => this._onSubmit(e);
    this.element.addEventListener('submit', this._submitHandler, { capture: true });
  }

  disconnect() {
    this.element.removeEventListener('submit', this._submitHandler, { capture: true });
  }

  async _onSubmit(event) {
    if (navigator.onLine) return; // online → let Rails/Turbo handle it

    event.preventDefault();
    event.stopImmediatePropagation();

    const form      = this.element;
    const formData  = new FormData(form);
    const method    = (formData.get('_method') || form.method || 'POST').toUpperCase();
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

    // Use offlineUrl value if set, otherwise fall back to form.action
    const url = this.hasOfflineUrlValue && this.offlineUrlValue
      ? this.offlineUrlValue
      : form.action;

    // Convert FormData → plain object for JSON serialisation (skip files)
    const payload = {};
    for (const [key, val] of formData.entries()) {
      if (key === '_method' || key === 'authenticity_token') continue;
      if (val instanceof File) continue; // files handled by offline-upload controller
      if (key.endsWith('[]')) {
        const k = key.slice(0, -2);
        payload[k] = payload[k] || [];
        payload[k].push(val);
      } else {
        payload[key] = val;
      }
    }

    const description = this.hasDescriptionValue && this.descriptionValue
      ? this.descriptionValue
      : this._inferDescription(payload);

    try {
      await enqueue({
        url,
        method:      method === 'GET' ? 'POST' : method,
        body:        JSON.stringify(payload),
        csrfToken,
        description,
        _legacy:     true,
        formPayload: payload
      });
    } catch (err) {
      console.warn('[offline-form] IndexedDB enqueue failed:', err);
    }

    window.dispatchEvent(new CustomEvent('taagger:queue-mutation', {
      detail: { url, method, body: JSON.stringify(payload), description, csrfToken }
    }));

    this._showConfirmation(description);
  }

  _inferDescription(payload) {
    if (this.hasTaskIdValue && this.taskIdValue) {
      return t('javascript.controllers.offline_form_controller.update_task', { id: this.taskIdValue });
    }
    if (payload['task[title]'] || payload.task?.title) {
      return t('javascript.controllers.offline_form_controller.create_task', { title: payload['task[title]'] || payload.task?.title || t('javascript.controllers.offline_form_controller.untitled') });
    }
    if (payload['comment[content]'] || payload.comment?.content || payload.comment?.plain_text) {
      return t('javascript.controllers.offline_form_controller.add_comment_queued');
    }
    return t('javascript.controllers.offline_form_controller.form_submission_queued');
  }

  _showConfirmation(description) {
    const msg = document.createElement('div');
    msg.className = 'rounded-xl border border-amber-300 bg-amber-50 p-6 text-center';
    msg.innerHTML = `
      <div class="text-amber-600 text-4xl mb-3">📡</div>
      <h3 class="font-bold text-gray-900 text-lg mb-1">${t('javascript.controllers.offline_form_controller.saved_for_sync')}</h3>
      <p class="text-gray-600 text-sm">${description}</p>
      <p class="text-gray-400 text-xs mt-2">${t('javascript.controllers.offline_form_controller.will_sync_when_online')}</p>
    `;
    this.element.replaceWith(msg);
  }
}
